// src/api/services/productWeight.ts
import { api } from './index';
import { Customer } from './customer';
import { Product } from './product';

// ProductWeight interfaces
export interface ProductWeight {
  id: number;
  code: string;
  weight: string;
  dateTime: Date;
  remark?: Date;
  product: Product;
  customer: Customer;
  headLicensePlate: string;
  tailLicensePlate?: string;
  truckWeight: number;
  truckWeightManualFlag: boolean;
  truckWeightDateTime?: Date;
  weightBy: string;
}

export interface ProductWeightCreateDTO {
  code: string;
  weight: string;
  dateTime: Date;
  remark?: Date;
  product: Product | number;
  customer: Customer | number;
  headLicensePlate: string;
  tailLicensePlate?: string;
  truckWeight: number;
  truckWeightManualFlag: boolean;
  truckWeightDateTime?: Date;
  weightBy: string;
}

export interface ProductWeightUpdateDTO {
  weight?: string;
  dateTime?: Date;
  remark?: Date;
  product?: Product | number;
  customer?: Customer | number;
  headLicensePlate?: string;
  tailLicensePlate?: string;
  truckWeight?: number;
  truckWeightManualFlag?: boolean;
  truckWeightDateTime?: Date;
  weightBy?: string;
}

// Query options interface
export interface QueryOptions {
  max?: number;
  offset?: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
  view?: string;
  additional?: string;
  [key: string]: any; // For custom filters
}

// Where query interface
export interface WhereQuery {
  where: string;
  params: Record<string, any>;
}

// Response interfaces
export interface ListResponse<T> {
  statusCode: number;
  data: T[];
  totalCount: number;
}

export interface SingleResponse<T> {
  statusCode: number;
  data: T;
}

// ProductWeight service
export const productWeightService = {
  /**
   * List all product weights with optional pagination and sorting
   */
  list: (options?: QueryOptions) =>
    api.get<ListResponse<ProductWeight>>('/productWeight', { params: options }),

  /**
   * Find product weights with complex filtering
   */
  where: (whereData: WhereQuery, options?: QueryOptions) =>
    api.post<ListResponse<ProductWeight>>('/productWeight/where', whereData, { 
      params: options 
    }),

  /**
   * Get a single product weight by ID
   */
  read: (id: number, options?: Pick<QueryOptions, 'view' | 'additional'>) =>
    api.get<SingleResponse<ProductWeight>>(`/productWeight/${id}`, { 
      params: options 
    }),

  /**
   * Create a new product weight record
   */
  create: (data: ProductWeightCreateDTO) =>
    api.post<SingleResponse<ProductWeight>>('/productWeight', data),

  /**
   * Update an existing product weight record
   */
  update: (id: number, data: ProductWeightUpdateDTO) =>
    api.put<SingleResponse<ProductWeight>>(`/productWeight/${id}`, data),

  /**
   * Delete a product weight record
   */
  delete: (id: number) =>
    api.delete<{ statusCode: number }>(`/productWeight/${id}`),
  
  /**
   * Helper method to find weights by date range
   */
  findByDateRange: (startDate: Date, endDate: Date, options?: QueryOptions) => {
    const whereData: WhereQuery = {
      where: "_.dateTime >= :startDate AND _.dateTime <= :endDate",
      params: { 
        startDate: startDate.toISOString(), 
        endDate: endDate.toISOString() 
      }
    };
    return productWeightService.where(whereData, options);
  },

  /**
   * Helper method to find weights by customer
   */
  findByCustomer: (customerId: number, options?: QueryOptions) => {
    const whereData: WhereQuery = {
      where: "customer.id = :customerId",
      params: { customerId }
    };
    return productWeightService.where(whereData, options);
  },

  /**
   * Helper method to find weights by product
   */
  findByProduct: (productId: number, options?: QueryOptions) => {
    const whereData: WhereQuery = {
      where: "product.id = :productId",
      params: { productId }
    };
    return productWeightService.where(whereData, options);
  },

  /**
   * Helper method to find by license plate
   */
  findByLicensePlate: (licensePlate: string, options?: QueryOptions) => {
    const whereData: WhereQuery = {
      where: "_.headLicensePlate = :licensePlate OR _.tailLicensePlate = :licensePlate",
      params: { licensePlate }
    };
    return productWeightService.where(whereData, options);
  }
};